import { LockFilled } from '@ant-design/icons';
import { Typography } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { workflowsApi } from '@/api/workflows';
import { PageHeader } from '@/components/PageHeader';
import { handleApiError } from '@/lib/handleApiError';

import { lockBannerCopy } from '../lockBannerCopy';
import { useWorkflowEditorSession } from '../WorkflowEditorSession';

import { DynamicFormEditorShell } from './DynamicFormEditorShell';
import { createWorkflowDataSource } from './FormEditorDataSource';

const { Text } = Typography;

/**
 * 3-panel form builder page per spec §6.1 + §6.5. Thin adapter: owns the real
 * workflow/step/lock context and route chrome, then hands a workflow-backed
 * data source to {@link DynamicFormEditorShell} for the actual palette/canvas/
 * properties UI (shared with the Custom Editor, which backs the same shell with
 * an in-memory data source instead).
 */
export function DynamicFormBuilderPage() {
  const { t, i18n } = useTranslation();
  const { id: templateId, stepId } = useParams<{ id: string; stepId: string }>();
  const navigate = useNavigate();

  const goBack = useCallback(() => {
    if (templateId) navigate(`/originator/workflows/${templateId}`);
    else navigate('/originator/dashboard');
  }, [navigate, templateId]);

  const { workflow, loading, lockBlock, patchWorkflow: setWorkflow } = useWorkflowEditorSession();

  const step = useMemo(() => {
    if (!workflow || !stepId) return null;
    const found = workflow.steps.find((s) => s.id === stepId);
    if (!found) return null;
    return { ...found, sections: found.sections ?? [] };
  }, [workflow, stepId]);

  // Refetch after every mutation — simple, correct, fine for v1 latency.
  const refresh = useCallback(async () => {
    if (!templateId) return;
    try {
      const fresh = await workflowsApi.get(templateId);
      setWorkflow(fresh);
    } catch (err) {
      handleApiError(err, t, t('workflow.details.loadFailed'));
    }
  }, [templateId, t, setWorkflow]);

  const dataSource = useMemo(
    () => (templateId && stepId ? createWorkflowDataSource(templateId, stepId, refresh) : null),
    [templateId, stepId, refresh],
  );

  // ---- Render ----

  if (loading || !workflow || !step || !dataSource) {
    return <div style={{ minHeight: 320, background: '#f5f7fa' }} />;
  }

  if (step.type !== 'DynamicForm') {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">{t('workflow.builder.errors.notDynamicForm')}</Text>
      </div>
    );
  }

  const stepTitle = step.titleEn || step.titleFr || t('workflow.builder.unnamedStep');
  const isLockedByOther = lockBlock !== null;
  // Headline only on this page — the builder banner is a single line, and the supporting
  // detail (expiry / "reopen to take the lock") lives on the Details page it came from.
  const lockCopy = lockBlock ? lockBannerCopy(lockBlock, t, i18n.language) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 600 }}>
      <PageHeader title={`${stepTitle}: ${t('workflow.builder.title')}`} onBack={goBack} />
      <Text type="secondary" style={{ padding: '0 4px 12px', fontSize: 13 }}>
        {t('workflow.builder.subtitle')}
      </Text>

      {lockCopy && (
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
            borderRadius: 10,
            background: 'linear-gradient(135deg, #fff7ed 0%, #fef3c7 100%)',
            border: '1px solid #fde68a',
            marginBottom: 12,
            fontSize: 13,
            color: '#7c2d12',
          }}
        >
          <LockFilled style={{ color: '#ad6800' }} />
          {lockCopy.title}
        </div>
      )}

      <DynamicFormEditorShell
        sections={step.sections}
        dataSource={dataSource}
        templateId={templateId}
        stepId={stepId}
        allSteps={workflow.steps}
        variables={workflow.variables}
        onPreview={() => navigate(`/originator/workflows/${templateId}/preview?stepId=${stepId}`)}
        disabled={isLockedByOther}
        maxHeight="75vh"
      />
    </div>
  );
}
